import { ReactNode, useMemo } from 'react';
import { useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { combineClasses } from '@/utils';

// Constants
import {
  ANIMATION_SPRING_CONFIGS,
  CLAMP_BOTH,
  EMPLOYEE_YEAR_IN_REVIEW_CONFIG,
  SCENE_3A_3B_OVERLAP_FRAMES,
  SCENE_3A_TEXT_FADE_OUT_DURATION,
} from '@/constants';

// Types
import { AnimationPreset } from '@/types';
import { Project } from '../types';

// Components
import { WaveTextReveal } from '@/components/common/animations/WaveTextReveal';
import { SlotMachineNumber } from '@/components/common/animations/SlotMachineNumber';
import { AnimatedSuffix } from '@/components/common/animations/AnimatedSuffix';
import { Flip3DBadge } from '@/components/common/animations/Flip3DBadge';

// ANIMATION TIMING CONFIGURATION
const TIMING = {
  START: 55, // Start animations after transition completes + extra 0.5s delay
  PARTICIPATED: { delay: 0, charStagger: 1.2 },
  PROJECTS_NUMBER: { delay: 28 },
  PROJECTS_TEXT: { delay: 45 },
  BADGE: { delay: 60 },
  OVER: { delay: 72, charStagger: 4 },
  MONTHS_NUMBER: { delay: 85 },
  MONTHS_TEXT: { delay: 100 },
};

// Transition timing - fade out starts before Scene 3 ends
const TRANSITION = {
  FADE_OUT_DURATION: SCENE_3A_TEXT_FADE_OUT_DURATION,
  FADE_OUT_START:
    EMPLOYEE_YEAR_IN_REVIEW_CONFIG.timing.slide3a.duration -
    SCENE_3A_3B_OVERLAP_FRAMES,
};

export interface TextSectionProps {
  projects: Project[];
  monthsOnProject?: number;
}

export const TextSection = ({
  projects,
  monthsOnProject = 0,
}: TextSectionProps): ReactNode => {
  const { typography, colors } = employeeYearInReviewTheme;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate statistics from projects
  const totalProjects = useMemo(() => projects.length, [projects]);
  const totalMonths = monthsOnProject >= 1 ? Math.floor(monthsOnProject) : 1;

  // Container entrance
  const containerProgress = spring({
    frame: frame - TIMING.START,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.CONTAINER],
  });

  const containerOpacity = interpolate(
    containerProgress,
    [0, 0.3],
    [0, 1],
    CLAMP_BOTH,
  );
  const containerTranslateY = interpolate(
    containerProgress,
    [0, 1],
    [20, 0],
    CLAMP_BOTH,
  );

  // Fade out opacity for transitioning elements ("Participated in", "over", badge)
  const fadeOutOpacity = interpolate(
    frame,
    [
      TRANSITION.FADE_OUT_START,
      TRANSITION.FADE_OUT_START + TRANSITION.FADE_OUT_DURATION,
    ],
    [1, 0],
    CLAMP_BOTH,
  );

  return (
    <div
      className="flex flex-col gap-16 w-full py-60 px-64"
      style={{
        opacity: containerOpacity,
        transform: `translateY(${containerTranslateY}px)`,
      }}
    >
      {/* "Participated in" - Wave 3D text reveal - FADES OUT */}
      <div className="pl-10" style={{ opacity: fadeOutOpacity }}>
        <WaveTextReveal
          text="Participated in"
          className={combineClasses(typography.base, colors.textPrimary)}
          startFrame={TIMING.START + TIMING.PARTICIPATED.delay}
          staggerFrames={TIMING.PARTICIPATED.charStagger}
          waveAmplitude={12}
        />
      </div>

      {/* Projects count - Slot machine number + animated suffix - FADES OUT */}
      <div
        className={combineClasses('text-right', {
          'pr-[93px]': totalProjects === 1,
        })}
        style={{ opacity: fadeOutOpacity }}
      >
        <SlotMachineNumber
          value={totalProjects}
          className={combineClasses(
            typography.base,
            colors.textPrimary,
            'pr-5',
          )}
          startFrame={TIMING.START + TIMING.PROJECTS_NUMBER.delay}
          spinDuration={15}
        />
        <AnimatedSuffix
          text={`project${totalProjects === 1 ? '' : 's'}`}
          className={combineClasses(typography.base, colors.textPrimary)}
          startFrame={TIMING.START + TIMING.PROJECTS_TEXT.delay}
        />
      </div>

      {/* Badge section with "over 8 months" - FADES OUT */}
      <div
        className="flex items-center gap-4"
        style={{ opacity: fadeOutOpacity }}
      >
        <div className="relative h-[131px]">
          {/* 3D Flip Badge Background */}
          <Flip3DBadge
            startFrame={TIMING.START + TIMING.BADGE.delay}
            className={combineClasses(
              colors.badgeBackground.secondary,
              'px-6 py-2 absolute h-[140px] top-8 right-0',
              monthsOnProject >= 1 ? 'w-[420px]' : 'w-[500px]',
            )}
          />
          {/* "over" text */}
          <WaveTextReveal
            text={`${monthsOnProject >= 1 ? 'over' : 'under'}`}
            className={combineClasses(
              typography.base,
              colors.textPrimary,
              'opacity-90 pr-5',
            )}
            startFrame={TIMING.START + TIMING.OVER.delay}
            staggerFrames={TIMING.OVER.charStagger}
            waveAmplitude={8}
          />
        </div>

        {/* Total months - Big slot machine number - STAYS VISIBLE */}
        <span className={combineClasses({ 'pl-[60px]': totalMonths === 1 })}>
          <SlotMachineNumber
            value={totalMonths}
            className={combineClasses(typography.base, colors.textPrimary)}
            startFrame={TIMING.START + TIMING.MONTHS_NUMBER.delay}
            spinDuration={18}
          />
          <AnimatedSuffix
            text={`month${totalMonths === 1 ? '' : 's'}`}
            className={combineClasses(
              typography.base,
              colors.textPrimary,
              'pl-5',
            )}
            startFrame={TIMING.START + TIMING.MONTHS_TEXT.delay}
          />
        </span>
      </div>
    </div>
  );
};
